/**
 * Single source of truth for which file types each upload surface accepts.
 *
 * Aligned with what the ingestion pipeline can actually parse (the RAGFlow naive
 * chunker for knowledge-base docs, and the markitdown extraction path used for
 * answer attachments) so an "accepted" upload never silently dies downstream.
 */

/** Extensions the KB pipeline and answer-attachment extraction both handle. */
export const KB_EXTENSIONS = ["pdf", "docx", "xls", "xlsx", "csv", "txt", "md", "pptx"] as const;

/** Chat attachments additionally allow images (stored and displayed, not chunked). */
export const CHAT_EXTENSIONS = [...KB_EXTENSIONS, "png", "jpg", "jpeg", "gif"] as const;

/** HTML `accept` attribute values. */
export const KB_ACCEPT = KB_EXTENSIONS.map((ext) => `.${ext}`).join(",");
export const CHAT_ACCEPT = CHAT_EXTENSIONS.map((ext) => `.${ext}`).join(",");

/** Mirrors the backend MAX_FILE_SIZE_MB ceiling — client-side pre-validation only. */
export const MAX_UPLOAD_SIZE_MB = 150;

/** Cap on attachments per onboarding answer; the first uploads as the answer file. */
export const MAX_FILES_PER_ANSWER = 5;

const normalize = (type: string): string => type.toLowerCase().replace(/^\./, "");

export const getFileExtension = (filename: string): string => {
  const idx = filename.lastIndexOf(".");
  return idx >= 0 ? filename.slice(idx + 1).toLowerCase() : "";
};

export const isKbSupported = (filename: string): boolean =>
  (KB_EXTENSIONS as readonly string[]).includes(getFileExtension(filename));

export const isChatSupported = (filename: string): boolean =>
  (CHAT_EXTENSIONS as readonly string[]).includes(getFileExtension(filename));

/**
 * Effective accept list for an onboarding question.
 * Question-declared types win; anything the pipeline cannot parse is filtered out.
 * A question that declares no types gets the full KB set; if filtering would leave
 * nothing, the declared list is kept as-is so the backend rejects it honestly
 * rather than the client silently widening the constraint.
 */
export const resolveOnboardingAccept = (declared?: string[] | null): string[] => {
  if (!declared || declared.length === 0) return [...KB_EXTENSIONS];
  const normalized = declared.map(normalize);
  const supported = normalized.filter((type) => (KB_EXTENSIONS as readonly string[]).includes(type));
  return supported.length > 0 ? supported : normalized;
};

/** Feedback screenshots are image-only (human triage, not ingestion) and
 * capped at 20MB — the backend enforces the same bound for feedback_attachment. */
export const FEEDBACK_EXTENSIONS = ["png", "jpg", "jpeg", "webp", "gif"] as const;
export const FEEDBACK_ACCEPT = FEEDBACK_EXTENSIONS.map((ext) => `.${ext}`).join(",");
export const FEEDBACK_MAX_SIZE_MB = 20;

export const isFeedbackSupported = (filename: string): boolean =>
  (FEEDBACK_EXTENSIONS as readonly string[]).includes(getFileExtension(filename));
